'use client';

import { Draft } from 'immer';
import posthog, { JsonType } from 'posthog-js';
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dlog } from 'src/dev';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useFeatureFlagStore, {
	FeatureFlagKey,
	FeatureFlagStore,
	FEATURE_FLAG_DEFAULTS,
} from './useFeatureFlagStore';
import useCurrentAuthority from 'src/hooks/useCurrentAuthority';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import { appendDbEnv } from 'src/utils/dbApiEnv';
import Env from 'src/environmentVariables/EnvironmentVariables';

// Use this to override and emulate flags being loaded asyncronously
const FLAG_OVERRIDES: Partial<FeatureFlagStore['flags']> = {};

/**
 * ⭐️⭐️⭐️⭐️ SEE readmes/feature_flags.md FOR MORE INFO ⭐️⭐️⭐️⭐️
 */

const RELOAD_REMOTE_FLAGS_EVERY_SESSION = true; // Whether to reload the feature flags for each session, otherwise it may use cached flags

/**
 * Default handler for feature flags that don't need custom handling
 */
const defaultFlagHandler = (
	stateDraft: Draft<FeatureFlagStore>,
	key: FeatureFlagKey,
	enabled: boolean,
	rawPayload?: unknown
) => {
	const hasObjectPayload =
		// @ts-expect-error
		!!rawPayload?.payload && typeof rawPayload?.payload === 'object';

	if (key in FLAG_OVERRIDES) {
		stateDraft.flags[key]._enabled = FLAG_OVERRIDES[key]?._enabled ?? false;
		stateDraft.flags[key].rawPayload = FLAG_OVERRIDES[key]?.rawPayload;
	} else {
		stateDraft.flags[key]._enabled = enabled;
		stateDraft.flags[key].rawPayload = rawPayload;

		if (hasObjectPayload) {
			// @ts-expect-error :: Expect the person creating the flag to ensure the payload matches the type in the store
			stateDraft.flags[key].payload = rawPayload?.payload;
		}
	}
};

/**
 * Type for custom flag handlers that need special processing
 */
type CustomFlagHandler = (
	stateDraft: Draft<FeatureFlagStore>,
	enabled: boolean,
	rawPayload?: unknown
) => void;

const getIsEnabledFromPayload = (payload: JsonType, flag: string) => {
	// Handle boolean values
	if (typeof payload === 'boolean') {
		return payload;
	}

	// Handle string values
	if (typeof payload === 'string') {
		return payload === 'true';
	}

	// Handle object with enabled field
	if (typeof payload === 'object' && payload !== null) {
		// If we're using our own custom payload flags then they will have an "enabled" field in the payload to determine if they should be enabled or not.
		if ('enabled' in payload) {
			return Boolean(payload.enabled);
		}

		// Otherwise, the payload may contain arbitrary data and we use posthog's default "isEnabled" to determine if the flag should be enabled or not.
		return posthog.isFeatureEnabled(flag);
	}

	return false;
};

const useSyncFeatureFlagStateInStore = () => {
	const setFeatureFlagStore = useFeatureFlagStore((s) => s.set);
	const getFeatureFlagStore = useFeatureFlagStore((s) => s.get);
	const appEventEmitter = useVelocityStore((s) => s.appEventEmitter);

	// Only define custom handlers for flags that need special processing
	const CUSTOM_FLAG_HANDLERS: Partial<
		Record<FeatureFlagKey, CustomFlagHandler>
	> = useMemo(
		() => ({
			HIGHLIGHTED_ASSETS: (
				stateDraft,
				enabled: boolean,
				rawPayload?: unknown
			) => {
				const assets =
					Array.isArray(rawPayload) &&
					rawPayload.every((asset) => typeof asset === 'string')
						? rawPayload
						: [];

				if (!Array.isArray(rawPayload) || assets.length === 0) {
					enabled = false;
				}

				defaultFlagHandler(
					stateDraft,
					'HIGHLIGHTED_ASSETS',
					enabled,
					rawPayload
				);
				stateDraft.flags.HIGHLIGHTED_ASSETS.assets = assets;
			},
			SHOWN_SPOT_MARKETS: (
				stateDraft,
				enabled: boolean,
				rawPayload?: unknown
			) => {
				const markets =
					Array.isArray(rawPayload) &&
					rawPayload.every((market) => typeof market === 'string')
						? rawPayload
						: [];

				if (!Array.isArray(rawPayload) || markets.length === 0) {
					enabled = false;
				}

				defaultFlagHandler(
					stateDraft,
					'SHOWN_SPOT_MARKETS',
					enabled,
					rawPayload
				);
				stateDraft.flags.SHOWN_SPOT_MARKETS.markets = markets;
			},
			MAKER_20K_REWARD_MARKET_INDEXES: (
				stateDraft,
				enabled: boolean,
				rawPayload?: unknown
			) => {
				const marketIndexes =
					Array.isArray(rawPayload) &&
					rawPayload.every((index) => typeof index === 'number')
						? rawPayload
						: [13, 71, 23, 4, 75, 59, 9, 7, 30, 62]; // Default fallback

				defaultFlagHandler(
					stateDraft,
					'MAKER_20K_REWARD_MARKET_INDEXES',
					enabled,
					rawPayload
				);
				stateDraft.flags.MAKER_20K_REWARD_MARKET_INDEXES.marketIndexes =
					marketIndexes;
			},
			ISOLATED_MARGIN_ENABLED_MARKETS: (
				stateDraft,
				enabled: boolean,
				rawPayload?: unknown
			) => {
				const marketSymbols =
					Array.isArray(rawPayload) &&
					rawPayload.every((symbol) => typeof symbol === 'string')
						? rawPayload
						: ['SOL-PERP', 'ETH-PERP', 'BTC-PERP']; // Default fallback

				if (!Array.isArray(rawPayload) || marketSymbols.length === 0) {
					enabled = false;
				}

				defaultFlagHandler(
					stateDraft,
					'ISOLATED_MARGIN_ENABLED_MARKETS',
					enabled,
					rawPayload
				);
				stateDraft.flags.ISOLATED_MARGIN_ENABLED_MARKETS.marketSymbols =
					marketSymbols;
			},
			AUCTION_PARAMS_VERSION: (
				stateDraft,
				enabled: boolean,
				rawPayload?: unknown
			) => {
				const rawVersion =
					typeof rawPayload === 'number'
						? rawPayload
						: typeof rawPayload === 'string'
							? Number(rawPayload)
							: typeof rawPayload === 'object' &&
								  rawPayload !== null &&
								  'version' in rawPayload
								? Number((rawPayload as { version: unknown }).version)
								: NaN;

				const version =
					Number.isFinite(rawVersion) && rawVersion > 0 ? rawVersion : 1;

				defaultFlagHandler(
					stateDraft,
					'AUCTION_PARAMS_VERSION',
					enabled,
					rawPayload
				);
				stateDraft.flags.AUCTION_PARAMS_VERSION.version = version;
			},
		}),
		[]
	);

	// Sync the enabled state of the feature flags in the store
	useEffect(() => {
		const flagsLoadedHandler = (_flags: string[]) => {
			const flags = Array.from(
				new Set([..._flags, ...Object.keys(FLAG_OVERRIDES)])
			); // Create unique list of flags including the overrides, in case they are not included in the response when fetching flags.

			const state = getFeatureFlagStore();

			setFeatureFlagStore((stateDraft) => {
				for (const flag of flags) {
					if (flag in state.flags) {
						const matchedFlagPayload = posthog.getFeatureFlagPayload(flag);

						const isEnabled = getIsEnabledFromPayload(matchedFlagPayload, flag);

						dlog(
							`feature_flags`,
							`flag_payload:: ${flag},isEnabled=${isEnabled}`,
							{
								payload: matchedFlagPayload,
							}
						);

						const flagKey = flag as FeatureFlagKey;
						const customHandler = CUSTOM_FLAG_HANDLERS[flagKey];

						if (customHandler) {
							customHandler(stateDraft, isEnabled ?? false, matchedFlagPayload);
						} else {
							defaultFlagHandler(
								stateDraft,
								flagKey,
								isEnabled ?? false,
								matchedFlagPayload
							);
						}

						stateDraft.flags[flagKey].hasBeenSynced = true;
					} else {
						dlog(`feature_flags`, `flag_not_in_store::${flag}`, flags);
					}
				}
			});
		};

		appEventEmitter.on('featureFlagsLoaded', flagsLoadedHandler);
		return () => {
			appEventEmitter.off('featureFlagsLoaded', flagsLoadedHandler);
		};
	}, [appEventEmitter, getFeatureFlagStore, setFeatureFlagStore]);
};

/**
 * Hook to fetch and sync custom feature flag overrides from the UI database.
 */
const useSyncCustomFeatureFlagOverrides = () => {
	const setFeatureFlagStore = useFeatureFlagStore((s) => s.set);
	const authority = useCurrentAuthority();
	const authorityStr = authority?.toString() ?? null;

	const queryFn = async (): Promise<FeatureFlagKey[]> => {
		if (!authorityStr) {
			return [];
		}

		const response = await fetch(
			appendDbEnv(`/api/feature-flags/overrides?authority=${authorityStr}`)
		);

		if (!response.ok) {
			throw new Error(
				`Failed to fetch custom feature flag overrides: ${response.status}`
			);
		}

		const json = await response.json();

		if (json.success && Array.isArray(json.data)) {
			const validFlags = json.data.filter(
				(flag: string) => flag in FEATURE_FLAG_DEFAULTS
			) as FeatureFlagKey[];
			return validFlags;
		}

		return [];
	};

	const { data } = useQuery({
		queryKey: createQueryKey('featureFlagOverrides', Env.sdkEnv, authorityStr),
		queryFn,
		enabled: !!authorityStr,
		staleTime: 60 * 60 * 1000, // Cache for 1 hour
		refetchOnWindowFocus: false,
	});

	// Syncs the custom feature flag overrides to the store.
	useEffect(() => {
		if (!authorityStr) {
			setFeatureFlagStore((stateDraft) => {
				stateDraft.customOverrides = new Set<FeatureFlagKey>();
			});
			return;
		}

		if (data) {
			const overrides = new Set<FeatureFlagKey>(data);
			setFeatureFlagStore((stateDraft) => {
				stateDraft.customOverrides = overrides;
			});
		}
	}, [authorityStr, data, setFeatureFlagStore]);
};

const useSyncFeatureFlags = () => {
	useEffect(() => {
		if (!RELOAD_REMOTE_FLAGS_EVERY_SESSION) return;
		posthog.reloadFeatureFlags();
	}, []);

	useSyncFeatureFlagStateInStore();
	useSyncCustomFeatureFlagOverrides();
};

export default useSyncFeatureFlags;
